/**
 * Market-parity helpers for AI triage: detector performance dashboard,
 * non-diagnostic labeling freeze, and report-impacting AI count gates.
 */

import {
  aggregateDetectorPerformance,
  type DetectorPerformanceCounters,
} from "./detectorPerfAndSeizureTemplates";
import { evaluateReportExplainability } from "./aiTriageExplainability";
import {
  AI_TRIAGE_EXCLUDED_USE,
  AI_TRIAGE_INTENDED_USE,
  type AITriageCandidate,
  type AITriageDispositionStatus,
} from "./aiTriageTypes";

export const AI_TRIAGE_MARKET_PARITY_SCHEMA_VERSION = "ai-triage-market-v1.0.0";

/** Phrases that must not appear as standalone diagnostic claims in production UI. */
export const AI_TRIAGE_FORBIDDEN_DIAGNOSTIC_CLAIMS = [
  "diagnosed seizure",
  "diagnosed epilepsy",
  "confirms epilepsy",
  "definitive seizure",
  "autonomous diagnosis",
  "replaces clinician review",
] as const;

export type AITriageLabelingBundle = {
  intendedUse: string;
  excludedUse: string;
  regulatoryPhase: 3;
  nonDiagnosticBadge: string;
  schemaVersion: string;
};

export function getAITriageLabelingBundle(): AITriageLabelingBundle {
  return {
    intendedUse: AI_TRIAGE_INTENDED_USE,
    excludedUse: AI_TRIAGE_EXCLUDED_USE,
    regulatoryPhase: 3,
    nonDiagnosticBadge: "Non-diagnostic decision support · Phase 3 gated",
    schemaVersion: AI_TRIAGE_MARKET_PARITY_SCHEMA_VERSION,
  };
}

export function assertNonDiagnosticCopy(text: string): {
  ok: boolean;
  violations: string[];
} {
  const lower = text.toLowerCase();
  const violations = AI_TRIAGE_FORBIDDEN_DIAGNOSTIC_CLAIMS.filter((phrase) =>
    lower.includes(phrase),
  );
  return { ok: violations.length === 0, violations: [...violations] };
}

export type AITriageDetectorDashboard = {
  schemaVersion: string;
  windowHours: number;
  falsePositivesPer24h: number;
  confirmationRate: number;
  confirmationRatePercent: number;
  totalCandidates: number;
  pending: number;
  confirmed: number;
  rejected: number;
  falsePositive: number;
  reportImpactingEligible: boolean;
  performance: DetectorPerformanceCounters;
};

function countByStatus(
  candidates: AITriageCandidate[],
  status: AITriageDispositionStatus,
): number {
  return candidates.filter((c) => c.disposition.status === status).length;
}

export function buildDetectorPerfDashboard(
  candidates: AITriageCandidate[],
  windowHours = 24,
): AITriageDetectorDashboard {
  const hours = Math.max(1, windowHours);
  const performance = aggregateDetectorPerformance(
    candidates.map((c) => ({
      category: c.category,
      status: c.disposition.status,
      detectedAtIso: c.disposition.reviewedAtIso ?? null,
    })),
    hours,
  );
  const confirmed = countByStatus(candidates, "confirmed");
  const pending = countByStatus(candidates, "pending");
  const rejected = countByStatus(candidates, "rejected");
  const falsePositive = countByStatus(candidates, "false_positive");

  return {
    schemaVersion: AI_TRIAGE_MARKET_PARITY_SCHEMA_VERSION,
    windowHours: hours,
    falsePositivesPer24h: performance.falsePositivesPer24h,
    confirmationRate: performance.confirmationRate,
    confirmationRatePercent: Number(
      (performance.confirmationRate * 100).toFixed(1),
    ),
    totalCandidates: candidates.length,
    pending,
    confirmed,
    rejected,
    falsePositive,
    reportImpactingEligible: canIncludeAiInReportCounts(candidates),
    performance,
  };
}

/**
 * Report-impacting AI counts require human confirmation — pending/FP/rejected
 * candidates must not inflate seizure/spike burden used in clinical reports.
 * When QC policy is enabled, technologist sign-off must also clear.
 */
export function canIncludeAiInReportCounts(
  candidates: AITriageCandidate[],
  options?: {
    qcSignOff?: { blocksPhysicianReporting: boolean; status?: string } | null;
    requireQcSignOff?: boolean;
    requireExplanation?: boolean;
  },
): boolean {
  if (candidates.length === 0) return false;
  const allConfirmed = candidates.every(
    (candidate) => candidate.disposition.status === "confirmed",
  );
  if (!allConfirmed) return false;
  if (options?.requireExplanation !== false) {
    if (!evaluateReportExplainability(candidates).ready) return false;
  }
  if (options?.requireQcSignOff === false) return true;
  if (options?.qcSignOff) {
    if (options.qcSignOff.blocksPhysicianReporting) return false;
    if (options.qcSignOff.status && options.qcSignOff.status !== "signed") {
      return false;
    }
  } else if (options?.requireQcSignOff) {
    return false;
  }
  return true;
}

export function filterConfirmedAiCandidatesForReport(
  candidates: AITriageCandidate[],
): AITriageCandidate[] {
  return candidates.filter((c) => c.disposition.status === "confirmed");
}

export type AITriageDispositionProvenanceSnapshot = {
  modelName: string;
  modelVersion: string;
  confidence: number | null;
  thresholds: Record<string, number | string | boolean>;
  recordingId: string | null;
  montageContext: string | null;
  filterContext: string | null;
  generatedAtIso: string;
};

export function buildDispositionProvenanceSnapshot(input: {
  candidate: AITriageCandidate;
  recordingId?: string | null;
  montageContext?: string | null;
  filterContext?: string | null;
  generatedAt?: Date;
}): AITriageDispositionProvenanceSnapshot {
  return {
    modelName: input.candidate.model.modelName,
    modelVersion: input.candidate.model.modelVersion,
    confidence: input.candidate.model.confidence ?? input.candidate.confidence,
    thresholds: { ...input.candidate.model.thresholds },
    recordingId: input.recordingId ?? null,
    montageContext: input.montageContext ?? null,
    filterContext: input.filterContext ?? null,
    generatedAtIso: (input.generatedAt ?? new Date()).toISOString(),
  };
}
